import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useStore from '@/store/useStore';
import PwaInstallPrompt from '@/components/guest/PwaInstallPrompt';
import { apiGuestSwitchStore } from '@/services/api';
import { getSelectedMerchantId, setSelectedMerchantId, storeRoute } from '@/lib/routes';

const ENTRY_MERCHANT_KEY = 'flyback_entry_merchant_id';
const ENTRY_MERCHANT_KEY_V2 = 'entry_merchant_id';

export default function StoreEntryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    guestTheme,
    guestToken,
    guestUser,
    setCurrentMerchantId,
    guestLogin,
    setWallet,
    setTransactions,
    setStoreInfo,
    setMerchantInfo,
  } = useStore();

  useEffect(() => {
    const incomingMerchantId = String(searchParams.get('merchant_id') || '').trim();
    if (incomingMerchantId) {
      window.localStorage.setItem(ENTRY_MERCHANT_KEY, incomingMerchantId);
      window.localStorage.setItem(ENTRY_MERCHANT_KEY_V2, incomingMerchantId);
      window.localStorage.removeItem('selected_merchant_id');
      setSelectedMerchantId(incomingMerchantId);
    }

    if (!guestToken) return undefined;

    let active = true;
    const syncSessionAndNavigate = async () => {
      const entryMerchantId =
        incomingMerchantId ||
        window.localStorage.getItem(ENTRY_MERCHANT_KEY) ||
        window.localStorage.getItem(ENTRY_MERCHANT_KEY_V2) ||
        '';
      if (entryMerchantId) {
        try {
          if (guestUser?.merchant_id !== entryMerchantId) {
            // QR scan while logged in: switch context directly without logout.
            setWallet(null);
            setTransactions([]);
            setStoreInfo(null);
            setMerchantInfo(null);
            const switched = await apiGuestSwitchStore(entryMerchantId);
            if (!active) return;
            guestLogin(switched.token, switched.customer, switched.merchant);
          }
          if (!active) return;
          setCurrentMerchantId(entryMerchantId);
          setSelectedMerchantId(entryMerchantId);
          window.localStorage.removeItem(ENTRY_MERCHANT_KEY);
          window.localStorage.removeItem(ENTRY_MERCHANT_KEY_V2);
          navigate(storeRoute(entryMerchantId, 'wallet'), { replace: true });
        } catch {
          if (!active) return;
          navigate('/store', { replace: true });
        }
        return;
      }

      const selectedMerchantId =
        getSelectedMerchantId();

      if (!selectedMerchantId || selectedMerchantId === 'demo') {
        navigate('/store/select', { replace: true });
        return;
      }

      let resolvedMerchantId = selectedMerchantId;
      try {
        if (guestUser?.merchant_id !== selectedMerchantId) {
          setWallet(null);
          setTransactions([]);
          setStoreInfo(null);
          setMerchantInfo(null);
          const switched = await apiGuestSwitchStore(selectedMerchantId);
          if (!active) return;
          guestLogin(switched.token, switched.customer, switched.merchant);
          resolvedMerchantId = switched?.merchant?.id || selectedMerchantId;
        }
      } catch {
        navigate('/store/select', { replace: true });
        return;
      }

      if (!active) return;
      const finalMerchantId = resolvedMerchantId;
      if (finalMerchantId && finalMerchantId !== 'demo') {
        setCurrentMerchantId(finalMerchantId);
        setSelectedMerchantId(finalMerchantId);
        navigate(storeRoute(finalMerchantId, 'wallet'), { replace: true });
      }
    };

    syncSessionAndNavigate();
    return () => {
      active = false;
    };
  }, [
    guestLogin,
    guestToken,
    guestUser?.merchant_id,
    navigate,
    searchParams,
    setCurrentMerchantId,
    setMerchantInfo,
    setStoreInfo,
    setTransactions,
    setWallet,
  ]);

  return (
    <div className={`${guestTheme === 'dark' ? 'dark ' : ''}relative min-h-screen flex flex-col`} data-testid="store-entry-page">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1671197346374-9d03a2a33a92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxhcHBldGl6aW5nJTIwcmVzdGF1cmFudCUyMGJsdXJyZWQlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NjIyOTYxOXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center max-w-md mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 w-full">
          <div className="w-16 h-16 rounded-2xl bg-stone-800/60 border border-stone-600/40 flex items-center justify-center mx-auto backdrop-blur-sm">
            <img src="/assets/flyback.png" alt="FlyBack" className="w-30 h-30 object-contain" />
          </div>

          <h1
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight"
            style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            FlyBack <span className="text-brand">Rewards</span>
          </h1>

          <p className="text-white/85 text-base">Log in and choose your restaurant to continue.</p>

          <button
            data-testid="entry-get-started-btn"
            onClick={() => navigate('/store/login')}
            className="w-full h-14 rounded-full gradient-brand-btn text-base font-semibold flex items-center justify-center gap-2"
          >
            Get Started <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-white/35 text-xs pt-3">
            Powered by <span className="font-semibold text-white/45">FlyBack</span>
          </p>
        </motion.div>
      </div>
      <PwaInstallPrompt />
    </div>
  );
}
